/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
// eslint-disable-next-line no-restricted-imports
import { difference, find, get } from 'lodash'
import { diff as deepDiff } from 'deep-diff'

import type { SliderInstanceType } from '@modules/ui-types'

import { isEnabledConfiguration } from '@/lib/deployments/conversion'

import { groupDiffs } from '../../diff'

import { getInstanceConfiguration } from './topology'
import { hasChangedPath, hasNext } from './preconditions'

import type {
  Difference,
  DifferenceArgs,
  DifferenceCheck,
  DifferenceType,
  FieldPathMapping,
} from './types'

type SettingsDiffArgs = {
  currentSettings: Map<string, any>
  nextSettings: Map<string, any>
} & DifferenceArgs

interface SettingsDiffBuilderArgs {
  fields: string[]
  defaultValue?: any
  diffSettings: (args: SettingsDiffArgs) => Array<Difference<any>>
}

export function buildSettingsDiffCheck({
  fields,
  defaultValue,
  diffSettings,
}: SettingsDiffBuilderArgs): DifferenceCheck {
  function pathBuilder(args: DifferenceArgs): FieldPathMapping[] {
    const basePaths = getBaseSettingsPaths(args)
    return fields.map((field) => ({
      field,
      possiblePaths: basePaths.map((basePath) => [...basePath, field]),
    }))
  }

  return {
    preconditions: [hasNext, hasChangedPath({ pathBuilder, defaultValue })],
    check: (args) => {
      const { current, next } = args
      const paths = pathBuilder(args)

      const currentSettings = new Map(
        paths.map(({ field, possiblePaths }) => {
          const foundValuePath = find<string[]>(possiblePaths, (possiblePath) =>
            get(current, possiblePath),
          )
          return [field, foundValuePath ? get(current, foundValuePath) : defaultValue]
        }),
      )
      const nextSettings = new Map(
        paths.map(({ field, possiblePaths }) => {
          const foundValuePath = find<string[]>(possiblePaths, (possiblePath) =>
            get(next, possiblePath),
          )
          return [field, foundValuePath ? get(next, foundValuePath) : defaultValue]
        }),
      )

      return diffSettings({
        ...args,
        currentSettings,
        nextSettings,
      })
    },
  }
}

interface SettingsDifferenceMeta {
  key?: string
  nextValue?: string
  currentValue?: string
}

export function processSettingsDiff({
  currentSettings,
  nextSettings,
  sliderInstanceType,
  addedType,
  changedType,
  removedType,
}: {
  currentSettings: any
  nextSettings: any
  sliderInstanceType: SliderInstanceType
  addedType: DifferenceType
  changedType: DifferenceType
  removedType: DifferenceType
}): Array<Difference<SettingsDifferenceMeta>> {
  const settingsDiff = deepDiff(currentSettings, nextSettings)
  const diffByKind = groupDiffs(settingsDiff)
  const settingsAdded = diffByKind.N.map(
    (setting): Difference<SettingsDifferenceMeta> => ({
      type: addedType,
      target: sliderInstanceType,
      meta: {
        key: setting.path && setting.path[0],
        nextValue: String(setting.rhs),
      },
    }),
  )

  const settingsChanged = diffByKind.E.map(
    (setting): Difference<SettingsDifferenceMeta> => ({
      type: changedType,
      target: sliderInstanceType,
      meta: {
        key: setting.path && setting.path[0],
        currentValue: setting.path && setting.lhs.toString(),
        nextValue: String(setting.rhs),
      },
    }),
  )

  const settingsRemoved = diffByKind.D.map(
    (setting): Difference<SettingsDifferenceMeta> => ({
      type: removedType,
      target: sliderInstanceType,
      meta: {
        key: setting.path && setting.path[0],
      },
    }),
  )

  return [...settingsAdded, ...settingsChanged, ...settingsRemoved]
}

export function processPluginOrBundleDiff({
  currentIds,
  nextIds,
  sliderInstanceType,
  addedType,
  removedType,
}: {
  currentIds: string[]
  nextIds: string[]
  sliderInstanceType: SliderInstanceType
  addedType: DifferenceType
  removedType: DifferenceType
}): Array<Difference<{ id: string }>> {
  const added = difference(nextIds, currentIds).map(
    (id): Difference<{ id: string }> => ({
      type: addedType,
      target: sliderInstanceType,
      meta: {
        id,
      },
    }),
  )
  const removed = difference(currentIds, nextIds).map(
    (id): Difference<{ id: string }> => ({
      type: removedType,
      target: sliderInstanceType,
      meta: {
        id,
      },
    }),
  )

  return [...added, ...removed]
}

function getBaseSettingsPaths({
  sliderInstanceType,
  next,
  current,
  instanceConfigurations,
}: DifferenceArgs): string[][] {
  // ES may have user settings at both plan level and topology level; other
  // sliders only use plan level
  if (sliderInstanceType !== `elasticsearch`) {
    return [[sliderInstanceType]]
  }

  if (!next?.cluster_topology) {
    return [[sliderInstanceType]]
  }

  if (!instanceConfigurations) {
    return [[sliderInstanceType]]
  }

  const topologyIndex = next.cluster_topology.findIndex((topologyElement, i) => {
    const currentTopologyElement = get(current, [`cluster_topology`, String(i)])
    const instanceConfiguration =
      getInstanceConfiguration(topologyElement, instanceConfigurations) ||
      getInstanceConfiguration(currentTopologyElement, instanceConfigurations)

    // Do not use this topology element if it's zero sized in both current and next plans
    if (
      topologyElement &&
      !isEnabledConfiguration(topologyElement) &&
      currentTopologyElement &&
      !isEnabledConfiguration(currentTopologyElement)
    ) {
      return false
    }

    return instanceConfiguration?.instance_type === sliderInstanceType
  })

  if (topologyIndex === -1) {
    return [[sliderInstanceType]]
  }

  // order matters here -- since we pick the first defined settings entry we
  // find, favour the plan level over the topology level
  return [[sliderInstanceType], [`cluster_topology`, String(topologyIndex), sliderInstanceType]]
}
